import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Loader2, Play, RefreshCw, TrendingUp, BookOpen, Library, CheckCircle2, XCircle, Activity, Clock, Pause } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { getEnrichmentStats, clearStatsCache, triggerBatchEnrichment, EnrichmentStats } from '@/utils/enrichmentStats';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

export function EnrichmentMonitoringDashboard() {
  const [stats, setStats] = useState<EnrichmentStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState(false);
  const [lastRun, setLastRun] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [nextScheduledRun, setNextScheduledRun] = useState<string>('');
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

  const loadStats = async (useCache: boolean = true) => {
    try {
      setLoading(true);
      const data = await getEnrichmentStats(useCache);
      setStats(data);
      setLastRefresh(new Date());
    } catch (error) {
      console.error('Failed to load stats:', error);
      toast.error('Failed to load enrichment statistics');
    } finally {
      setLoading(false);
    }
  };

  const handleForceRefresh = async () => {
    clearStatsCache();
    await loadStats(false);
    toast.success('Statistics refreshed');
  };

  // Calculate next scheduled run (every 10 minutes)
  useEffect(() => {
    const updateNextRun = () => {
      const now = new Date();
      const currentMinute = now.getMinutes();
      const nextTenMinute = Math.ceil((currentMinute + 1) / 10) * 10;
      const next = new Date(now);
      
      if (nextTenMinute >= 60) {
        next.setHours(next.getHours() + 1, 0, 0, 0);
      } else {
        next.setMinutes(nextTenMinute, 0, 0);
      }
      
      const minutes = Math.floor((next.getTime() - now.getTime()) / 60000);
      setNextScheduledRun(`in ${minutes} minutes`);
    };
    
    updateNextRun();
    const interval = setInterval(updateNextRun, 60000); // Update every minute
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    loadStats();
    
    if (!autoRefresh) return;
    
    // Refresh every 30 seconds
    const interval = setInterval(loadStats, 30000);
    return () => clearInterval(interval);
  }, [autoRefresh]);

  const handleTriggerEnrichment = async () => {
    try {
      setTriggering(true);
      toast.info('Starting batch enrichment...');
      
      const result = await triggerBatchEnrichment();

      if (!result || result.success === false) {
        const message = result?.error || 'Batch enrichment failed. Please check the edge function logs.';
        toast.error(message);
      } else if (result.enriched === 0 && result.failed > 0) {
        toast.error(`Enrichment failed for all ${result.failed} books. Check logs for details.`);
      } else {
        toast.success(`Enrichment complete! ${result.enriched} books enriched, ${result.failed} failed`);
      }

      setLastRun(new Date().toLocaleTimeString());
      
      // Reload stats after enrichment
      await loadStats();
    } catch (error) {
      console.error('Failed to trigger enrichment:', error);
      toast.error('Failed to start enrichment');
    } finally {
      setTriggering(false);
    }
  };

  if (loading || !stats) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  const completionRate = stats.totalBooks > 0 
    ? Math.round((stats.hasSynopsis / stats.totalBooks) * 100) 
    : 0;

  const seriesRate = stats.totalBooks > 0
    ? Math.round((stats.hasSeries / stats.totalBooks) * 100)
    : 0;

  const pageCountRate = stats.totalBooks > 0
    ? Math.round((stats.hasPageCount / stats.totalBooks) * 100)
    : 0;

  const isbnRate = stats.totalBooks > 0
    ? Math.round((stats.hasIsbn / stats.totalBooks) * 100)
    : 0;

  return (
    <div className="space-y-6">
      {/* Header Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Enrichment Monitoring Dashboard</CardTitle>
              <CardDescription>
                Tracking of book data enrichment. Stats cached for 5 minutes (approx. values based on 10k sample).
                <span className="flex items-center gap-1 text-xs mt-1 text-muted-foreground">
                  <Clock className="h-3 w-3" />
                  Last refresh: {lastRefresh.toLocaleTimeString()}
                </span>
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleForceRefresh}
                disabled={loading}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Force Refresh
              </Button>
              <Button
                onClick={handleTriggerEnrichment}
                disabled={triggering || stats.needsEnrichment === 0}
                size="sm"
              >
                {triggering ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Play className="h-4 w-4 mr-2" />
                    Run Enrichment
                  </>
                )}
              </Button>
            </div>
          </div>

          {nextScheduledRun && (
            <Alert className="mt-4">
              <Clock className="h-4 w-4" />
              <AlertDescription>
                <div className="flex items-center justify-between">
                  <span>Automated enrichment runs every 10 minutes</span>
                  <Badge variant="secondary">Next run: {nextScheduledRun}</Badge>
                </div>
              </AlertDescription>
            </Alert>
          )}
        </CardHeader>
        <CardContent>
          {lastRun && (
            <p className="text-sm text-muted-foreground">
              Last run: {lastRun}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Progress Overview */}
      <Card>
        <CardHeader>
          <CardTitle>Overall Progress</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Data Completion</span>
              <span className="text-sm text-muted-foreground">{completionRate}%</span>
            </div>
            <Progress value={completionRate} className="h-2" />
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4">
            <div>
              <div className="text-2xl font-bold">{stats.hasSynopsis.toLocaleString()}</div>
              <div className="text-xs text-muted-foreground">
                With Synopsis
                {completionRate < 50 && (
                  <div className="mt-1 text-orange-600">
                    {(stats.totalBooks - stats.hasSynopsis).toLocaleString()} remaining
                  </div>
                )}
              </div>
            </div>
            <div>
              <div className="text-2xl font-bold">{stats.hasSeries.toLocaleString()}</div>
              <div className="text-xs text-muted-foreground">With Series</div>
            </div>
            <div>
              <div className="text-2xl font-bold">{stats.hasPageCount.toLocaleString()}</div>
              <div className="text-xs text-muted-foreground">With Page Count</div>
            </div>
            <div>
              <div className="text-2xl font-bold">{stats.hasIsbn.toLocaleString()}</div>
              <div className="text-xs text-muted-foreground">With ISBN</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Status Grid */}
      <div className="grid md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <BookOpen className="h-4 w-4" />
              Total Books
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{stats.totalBooks.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground mt-1">
              In database
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Library className="h-4 w-4" />
              User Libraries
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{stats.inUserLibraries.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Books in user collections
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              Needs Enrichment
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-orange-600">
              {stats.needsEnrichment.toLocaleString()}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              ~{Math.ceil((stats.totalBooks - stats.hasSynopsis) / 500)} hours at 500/hour
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Enrichment Status */}
      <Card>
        <CardHeader>
          <CardTitle>Enrichment Status</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="flex items-center gap-3 p-3 border rounded-lg">
              <div className="h-10 w-10 rounded-full bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center">
                <Loader2 className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
              </div>
              <div>
                <div className="text-2xl font-bold">{stats.enrichmentPending.toLocaleString()}</div>
                <div className="text-sm text-muted-foreground">Pending</div>
              </div>
            </div>

            <div className="flex items-center gap-3 p-3 border rounded-lg">
              <div className="h-10 w-10 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <div className="text-2xl font-bold">{stats.enrichmentComplete.toLocaleString()}</div>
                <div className="text-sm text-muted-foreground">Complete</div>
              </div>
            </div>

            <div className="flex items-center gap-3 p-3 border rounded-lg">
              <div className="h-10 w-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                <XCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <div className="text-2xl font-bold">{stats.enrichmentFailed.toLocaleString()}</div>
                <div className="text-sm text-muted-foreground">Failed</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Data Quality Metrics */}
      <Card>
        <CardHeader>
          <CardTitle>Data Quality Metrics</CardTitle>
          <CardDescription>Percentage of books with complete metadata</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm">Synopsis Coverage</span>
              <Badge variant="outline">{completionRate}%</Badge>
            </div>
            <Progress value={completionRate} />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm">Series Information</span>
              <Badge variant="outline">{seriesRate}%</Badge>
            </div>
            <Progress value={seriesRate} />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm">Page Count Data</span>
              <Badge variant="outline">{pageCountRate}%</Badge>
            </div>
            <Progress value={pageCountRate} />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm">ISBN Coverage</span>
              <Badge variant="outline">{isbnRate}%</Badge>
            </div>
            <Progress value={isbnRate} />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
